import { Label, inputVariants } from '@sr/ui-library';
import { useCallback, useId, useMemo } from 'react';
import Autocomplete from 'react-google-autocomplete';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { configuration } from '../../../../configuration';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsFormValues } from '../../shared/types';

type GoogleAddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

export type GooglePlaceResult = {
  address_components?: GoogleAddressComponent[];
};

/**
 * Map a Google Places result onto the address form values. Ported from the
 * legacy AddressDetailsForm.handlePlaceSelected (lines 61-110): street_number
 * + route → line1, postal_town + locality → city, admin_area_level_1 long
 * name → state, country short_name (ISO-2) → country.
 */
export const mapGooglePlaceToAddressValues = (place: GooglePlaceResult) => {
  // `types` is an unordered set per the Places API contract — a component's
  // specific type is usually first but not guaranteed (e.g. sublocality
  // arrives as ['political', 'sublocality', ...]), so match by membership.
  const component = (type: string) =>
    place.address_components?.find((entry) => entry.types.includes(type));

  const streetNumber = component('street_number');
  const route = component('route');
  const postalTown = component('postal_town');
  const locality = component('locality');

  return {
    addressCity: `${postalTown?.long_name || ''} ${
      locality?.long_name || ''
    }`.trim(),
    addressCountry: component('country')?.short_name || '',
    addressLine1: `${streetNumber?.long_name || ''} ${
      route?.long_name || ''
    }`.trim(),
    addressPostcode: component('postal_code')?.long_name || '',
    addressState: component('administrative_area_level_1')?.long_name || '',
  };
};

interface AddressSearchFieldProps {
  form: UseFormReturn<WorkspaceDetailsFormValues>;
  /** Bumped by the parent on save/Discard so the uncontrolled search input remounts empty. */
  resetKey?: number;
}

/**
 * Optional Google Places search. Picking a result autofills country, address,
 * city, state and postcode (all remain editable). Results are restricted to
 * the selected country, matching legacy behaviour.
 *
 * Deliberately NOT a react-hook-form field: the search text is a pure UI
 * convenience that must never dirty the form (a dirty flag here would arm the
 * PageHeader Save button and trigger a PATCH with no persistable change).
 * The input is uncontrolled; only a place selection writes into the form.
 * `resetKey` remounts the input after save or Discard so leftover search text
 * does not linger.
 *
 * Renders react-google-autocomplete directly (not the legacy
 * GooglePlaceAutocomplete component, which hardwires v2 Controller/
 * InputWrapper markup) with ui-library input styling.
 */
export const AddressSearchField = ({
  form,
  resetKey = 0,
}: AddressSearchFieldProps) => {
  const inputId = useId();
  const country = useWatch({
    control: form.control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.addressCountry,
  });

  const handlePlaceSelected = useCallback(
    (place: GooglePlaceResult) => {
      // Pressing Enter without picking a suggestion fires place_changed with a
      // PlaceResult that has no address_components — bail instead of wiping
      // the five address fields with empty strings (legacy threw here).
      if (!place.address_components?.length) return;

      const mapped = mapGooglePlaceToAddressValues(place);

      (Object.keys(mapped) as (keyof typeof mapped)[]).forEach((name) =>
        form.setValue(WORKSPACE_DETAILS_FORM_FIELDS[name], mapped[name], {
          shouldDirty: true,
          shouldValidate: true,
        }),
      );
    },
    [form],
  );

  const options = useMemo(
    () => ({
      ...(country
        ? { componentRestrictions: { country: country.toLowerCase() } }
        : {}),
      fields: ['address_components'],
      types: ['address'],
    }),
    [country],
  );

  // space-y-3 mirrors ui-library FormItem so this row aligns with siblings.
  return (
    <div className="space-y-3">
      <Label htmlFor={inputId}>Address Search</Label>

      <Autocomplete
        id={inputId}
        key={resetKey}
        apiKey={configuration.GOOGLE_PLACES_API_KEY}
        className={inputVariants()}
        language="en"
        options={options}
        placeholder="Google Search"
        onPlaceSelected={handlePlaceSelected}
      />
    </div>
  );
};
